import { FieldKey, OptionKey } from '@core/data-access/models/dynamic-form-field/dynamic-form-field.model';
import {
  CheckboxFormField,
  FormField,
  FormFields,
  FormFieldValue
} from '@core/data-access/models/form/form-store.model';
import { FormValue, FormValues, QuestionnaireFormValue } from '@core/data-access/models/form/form.model';
import { DynamicFieldOptionsTranslationFactory } from '@core/data-access/services/dynamic-form-field/dynamic-field-options-translation-factory.service';
import { toUTCDate } from '@core/utils/datetime-helpers';
import { getQuestionnaireFormFieldDynValueFieldKey } from '@core/utils/form-fields/form-field-helpers';
import { sanitizeString } from '@core/utils/helpers';
import { TranslateService } from '@ngx-translate/core';
import { AppConstants } from '@shared/app-constants';
import { NewsletterInputType } from '@store/customization/customization.interfaces';
import {
  find,
  forEach,
  includes,
  isArray,
  isBoolean,
  isDate,
  isEmpty,
  isNil,
  isNull,
  isObject,
  isString,
  last,
  map,
  parseInt,
  reduce,
  split,
  startsWith
} from 'lodash';

export const mapResponseValueToFormFields = (
  formFields: FormFields,
  fieldKey: FieldKey,
  fieldValue: string,
  translateService: TranslateService,
  dynamicFieldOptionsTranslationFactory: DynamicFieldOptionsTranslationFactory
) => {
  if (fieldKey === FieldKey.DateOfBirth) {
    formFields[fieldKey] = {
      value: !isEmpty(fieldValue) ? new Date(fieldValue) : fieldValue,
      isQuestionnaire: false,
      isSaved: true
    };

    return formFields;
  }

  if (fieldKey === FieldKey.Email) {
    formFields[FieldKey.MailConfirm] = {
      value: fieldValue,
      isQuestionnaire: false,
      isSaved: true
    };
  }

  formFields[fieldKey] = {
    value: checkFieldValueTranslationsAndSetToFieldOptionValue(
      fieldValue,
      fieldKey,
      translateService,
      dynamicFieldOptionsTranslationFactory
    ),
    isQuestionnaire: false,
    isSaved: true
  };

  return formFields;
};

export const mapNewsletterTypeToFormField = (
  newsletterInputType: NewsletterInputType,
  radioButtonOptionLabels: {
    optIn: string;
    optOut: string;
  },
  isNewsletterChecked: boolean
): FormField | CheckboxFormField => {
  if (newsletterInputType === NewsletterInputType.Hidden) {
    return null;
  }

  if (newsletterInputType === NewsletterInputType.RadioButtonUnselected) {
    const { optIn, optOut } = radioButtonOptionLabels;

    return {
      value: isNewsletterChecked ? optIn : optOut,
      isQuestionnaire: false
    };
  }

  return {
    value: {
      [OptionKey.IsNewsletterChecked]: {
        value: isNewsletterChecked,
        isQuestionnaire: false
      }
    },
    isQuestionnaire: false
  };
};

export const sanitizeAndNormalizeFormValues = <T>(formValues: FormValues, translateService?: TranslateService): T =>
  reduce(
    formValues,
    (normalizedValues, value, key: FieldKey) => {
      const removeFormFields = [
        FieldKey.Address,
        FieldKey.MailConfirm,
        FieldKey.VerifyPassword,
        FieldKey.CreateAccount,
        FieldKey.CreateAccountButton,
        FieldKey.GDPR
      ];

      if (includes(removeFormFields, key)) {
        return normalizedValues;
      }

      if (isEmptyFormValue(value)) {
        normalizedValues[key] = value;

        return normalizedValues;
      }

      if (isObject(value)) {
        if (isDate(value)) {
          normalizedValues[key] = toUTCDate(value);

          return normalizedValues;
        }

        if (isArray(value) && includes([FieldKey.CV], key)) {
          normalizedValues[key] = map(value, 'id');

          return normalizedValues;
        }

        forEach(value, (checkbox, checkboxKey) => {
          normalizedValues[checkboxKey] = checkbox;
        });

        return normalizedValues;
      }

      if (isBoolean(value)) {
        normalizedValues[key] = value;

        return normalizedValues;
      }

      if (isString(value) && FieldKey.Newsletter === key) {
        normalizedValues[OptionKey.IsNewsletterChecked] = !includes(value, 'opt-out');

        return normalizedValues;
      }

      if (isNil(translateService)) {
        normalizedValues[key] = sanitizeString(value);

        return normalizedValues;
      }

      if (isString(value) && includes([FieldKey.Gender, FieldKey.Country], key)) {
        normalizedValues[key] = removeFormOptionValueTranslationPrefix(value);

        return normalizedValues;
      }

      normalizedValues[key] = sanitizeString(
        checkOptionValueAndSetToTranslatedFieldValue(value, key, translateService)
      );

      return normalizedValues;
    },
    {}
  ) as T;

export const sanitizeAndNormalizeQuestionnaireFormValues = <T>(formValues: FormValues): T =>
  reduce(
    formValues,
    (normalizedValues, value, key) => {
      if (isEmptyFormValue(value)) {
        return normalizedValues;
      }

      if (isObject(value)) {
        forEach(value, (checkboxChecked, checkboxKey) => {
          if (!checkboxChecked) {
            return;
          }

          normalizedValues[`${key}_${checkboxKey}`] = {
            fieldId: parseInt(key),
            valueId: parseInt(checkboxKey),
            text: null
          };
        });

        return normalizedValues;
      }

      const questionnaireFormValue: QuestionnaireFormValue = {
        fieldId: parseInt(key),
        valueId: null,
        text: null
      };

      if (isString(value)) {
        if (startsWith(value, 'DynValue.')) {
          questionnaireFormValue.valueId = getQuestionnaireFormFieldDynValueFieldKey(value);

          normalizedValues[key] = questionnaireFormValue;

          return normalizedValues;
        }

        const stringValue = split(key, '_');

        if (!isEmpty(stringValue)) {
          const [fieldId, valueId] = stringValue;

          questionnaireFormValue.fieldId = parseInt(fieldId);
          questionnaireFormValue.valueId = parseInt(valueId);
          questionnaireFormValue.text = sanitizeString(value);

          normalizedValues[fieldId] = questionnaireFormValue;

          return normalizedValues;
        }
      }

      return normalizedValues;
    },
    {}
  ) as T;

export const checkOptionValueAndSetToTranslatedFieldValue = (
  fieldValue: string,
  fieldKey: string,
  translateService: TranslateService
) => {
  if (isEmptyFormValue(fieldValue)) {
    return fieldValue;
  }

  const translateFieldKeys = [FieldKey.Function, FieldKey.Department, FieldKey.OccupationalGroup, FieldKey.Title];
  const translateFieldFieldValue = includes(translateFieldKeys, fieldKey);

  if (translateFieldFieldValue) {
    const translatedValue = translateService.instant(fieldValue);
    const isTranslatable = translatedValue !== AppConstants.MISSING_TRANSLATION;

    return isTranslatable ? translatedValue : fieldValue;
  }

  return fieldValue;
};

export const checkFieldValueTranslationsAndSetToFieldOptionValue = (
  fieldValue: string,
  fieldKey: string,
  translateService: TranslateService,
  dynamicFieldOptionsTranslationFactory: DynamicFieldOptionsTranslationFactory
) => {
  if (isEmptyFormValue(fieldValue)) {
    return fieldValue;
  }

  const translateFieldKeys = [
    FieldKey.Function,
    FieldKey.Department,
    FieldKey.OccupationalGroup,
    FieldKey.Title,
    FieldKey.Gender,
    FieldKey.Country
  ];

  const translateFieldFieldValue = includes(translateFieldKeys, fieldKey);

  if (translateFieldFieldValue) {
    const isGenderOrCountryField = includes([FieldKey.Gender, FieldKey.Country], fieldKey);
    const translationFieldKey = isGenderOrCountryField
      ? combineTranslatedFormOptionKeyAndValue(fieldKey, fieldValue)
      : fieldValue;

    const isTranslatable = translateService?.instant(translationFieldKey) !== AppConstants.MISSING_TRANSLATION;

    if (isTranslatable) {
      return translationFieldKey;
    }

    const dynamicFieldTranslatedOptions = dynamicFieldOptionsTranslationFactory.state();
    const dynamicFieldTranslatedOptionValue = find(dynamicFieldTranslatedOptions.allOptions, {
      label: fieldValue
    })?.value;

    if (!isNil(dynamicFieldTranslatedOptionValue)) {
      return dynamicFieldTranslatedOptionValue;
    }

    const dropdownTranslationExists = !isNil(dynamicFieldTranslatedOptions[fieldKey]);

    return isGenderOrCountryField || !dropdownTranslationExists ? fieldValue : '';
  }

  return fieldValue;
};

export const isEmptyFormValue = (formValue: FormValue) =>
  (isEmpty(formValue) || isNull(formValue)) && !isDate(formValue);

export const removeFormOptionValueTranslationPrefix = (value: string) =>
  includes(value, '.') ? last(split(value, '.')) : value;

export const combineTranslatedFormOptionKeyAndValue = (key: FieldKey | string, value: FormFieldValue | string) =>
  `${key}.${value}`;
